import { useEffect } from "react";
import { useOnboarding } from "@/features/onboarding";
import { useError } from "@/providers/Error";
import { emailOtpMessages, genericMessages } from "@/constants/errorMessages";
import { useEmailValidation } from "./useEmail";

const errorMessage = (error: string | undefined) =>
  emailOtpMessages[error as keyof typeof emailOtpMessages] ||
  genericMessages.unexpected;

// Confirms the emailed code (and sends a new one) for the email confirmation
// page and the chat email step.
export const useEmailCode = ({
  onError,
}: { onError?: (message: string) => void } = {}) => {
  const { setError } = useError();
  // The chat answers errors in the conversation instead of the popover.
  const reportError = onError ?? setError;
  const { getOnboardingState } = useOnboarding();
  const {
    requestEmailValidation,
    validateEmail,
    resendDelay,
    validateEmailError,
    requestEmailValidationError,
    isLoading,
  } = useEmailValidation();

  useEffect(() => {
    if (validateEmailError || requestEmailValidationError) {
      reportError(genericMessages.unexpected);
    }
  }, [validateEmailError, requestEmailValidationError]);

  // Resolves true once the email is validated and the onboarding state refreshed.
  const confirmCode = async (code: string) => {
    let result;
    try {
      result = await validateEmail(code);
    } catch {
      // Reported by the mutation error effect.
      return false;
    }
    if (!result.isSuccess) {
      reportError(errorMessage(result.error));
      return false;
    }

    await getOnboardingState();
    return true;
  };

  const resendCode = async () => {
    let result;
    try {
      result = await requestEmailValidation();
    } catch {
      // Reported by the mutation error effect.
      return false;
    }
    if (!result.isSuccess) {
      reportError(errorMessage(result.error));
      return false;
    }
    return true;
  };

  return { confirmCode, resendCode, resendDelay, isLoading };
};
